import { Button, Col, DatePicker, Form, notification, Popconfirm, Row, Select, Spin, Timeline } from 'antd'
import dayjs from 'dayjs'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { Icon } from '../../../../components'
import { handleBackendError } from '../../../../utils'
import { getUserFilteredList, getUserList, updateUser } from '../../../users/api'

export const NewCorporateUserForm = ({ resetSelectedMenu, selectedStore }) => {
  const [isLoading, setIsLoading] = useState(false)

  const [form] = Form.useForm()
  const [filteredUserList, setFilteredUsers] = useState([])
  const [usersWithDepartuteDate, setUsersWithDepartuteDate] = useState([])
  const [fetching, setFetching] = useState(false)

  useEffect(() => {
    fetchUserList()
  }, [])

  const confirmCancelLeaving = (id) => {
    const fieldsValue = {
      id: id,
      store_departure_date: null,
      store_id: selectedStore.id,
    }

    updateUserApi(fieldsValue, false)
  }

  const UserWithRemoveOption = ({ id, name }) => (
    <>
      {name}
      <Popconfirm
        cancelText="No"
        description={`Are you sure you want cancel "${name}"" leaving?`}
        okText="Yes"
        onConfirm={() => confirmCancelLeaving(id)}
        title="Cancel user leaving"
      >
        <Icon style={{ color: 'red' }} type="delete" />
      </Popconfirm>
    </>
  )

  UserWithRemoveOption.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
  }

  const fetchUserList = () => {
    getUserList(
      `country_id=${selectedStore.country_id}&store_name=${selectedStore.name}&items_per_page=10&page=1&sort_by=created_at+DESC`,
    )
      .then(({ data }) => {
        const dataForTimeLine = data.data
          .filter((user) => !!user.store_departure_date)
          .map((user) => ({
            content: <UserWithRemoveOption id={user.id} name={user.name} />,
            icon: <Icon type="clock" />,
            title: dayjs(user.store_departure_date).format('DD.MM.YYYY'),
          }))
        setUsersWithDepartuteDate(dataForTimeLine)
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(error, 'Fetching user list failed!'),
          title: 'Error',
        })
      })
  }

  const onUserSearch = (searchValue) => {
    if (!form.getFieldValue('country_id') || searchValue.length < 1) return null

    const searchParam = `id_or_name_or_email=${searchValue}&country_id=${selectedStore.country_id}&store_id=${selectedStore.id}`
    setFetching(true)

    getUserFilteredList(searchParam)
      .then(({ data }) => {
        const result = data.data.map((user) => ({
          label: `${user.name} (${user.email})`,
          value: user.id,
        }))

        setFilteredUsers(result)
        setFetching(false)
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(error, 'Fetching user list failed!'),
          title: 'Error',
        })

        setFetching(false)
      })
  }

  const handleSubmit = () => {
    const isFormUpdated = form.isFieldsTouched()

    if (isFormUpdated) {
      form.validateFields().then(() => {
        setIsLoading(true)
        const selectedDate = form.getFieldValue('departure_date')

        const fieldsValue = {
          id: form.getFieldValue('user_id'),
          store_departure_date: selectedDate ? dayjs(selectedDate).format('YYYY-MM-DD') : null,
          store_id: selectedDate ? selectedStore.id : null,
        }

        updateUserApi(fieldsValue)
      })
    }
  }

  const updateUserApi = (fieldsValue, isNeedResetMenu = true) => {
    updateUser(fieldsValue)
      .then(() => {
        notification.success({
          description: 'Corporate user restriction apply successfully',
          duration: 5,
          title: 'Success',
        })

        if (isNeedResetMenu) {
          resetSelectedMenu()
        } else {
          fetchUserList()
        }
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(error, 'Corporate user leaving failed!'),
          title: 'Error',
        })
        setIsLoading(false)
      })
  }

  return (
    <Row style={{ marginTop: '20px' }}>
      <Col md={14} xs={24}>
        <Form
          data-test="manager-leaving-form"
          form={form}
          initialValues={{ country_id: selectedStore.country_id }}
          layout="vertical"
        >
          <Form.Item
            label="User name or email"
            name="user_id"
            rules={[{ message: 'Please select a user!', required: true }]}
          >
            <Select
              data-testid="user_id"
              notFoundContent={fetching ? <Spin size="small" /> : null}
              options={filteredUserList}
              showSearch={{
                filterOption: false,
                onSearch: debounce(onUserSearch, 1000),
              }}
            />
          </Form.Item>

          <Form.Item
            help="Restrictions apply at midnight of selected date. If left empty it is immediate."
            label="Departure date"
            name="departure_date"
          >
            <DatePicker format="DD.MM.YYYY" minDate={dayjs()} />
          </Form.Item>
        </Form>
      </Col>

      <Col md={10} xs={24}>
        <Timeline items={usersWithDepartuteDate} mode="start" />
      </Col>

      <Col offset={9} span={6} style={{ marginTop: 14 }}>
        <Button block disabled={isLoading} htmlType="submit" onClick={handleSubmit} type="primary">
          Submit
        </Button>
      </Col>
    </Row>
  )
}

NewCorporateUserForm.propTypes = {
  resetSelectedMenu: PropTypes.func,
  selectedStore: PropTypes.object,
}

export default NewCorporateUserForm
